import type { AppSelectGroup } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AppInput, AppSelect, AppSelectPopover, AppTextarea, type SelectOption } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Label } from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { FIELD_MAX_LENGTHS } from 'shared'
import type { CloseReason, OpportunityStatus } from '@/lib'
import { COLOR_CATEGORY, getColor } from '@/lib/color-registry'

export interface OpportunityStatusState {
  statusId: number
  closeReasonId: number | null
  additionalCloseReason: string
  gCompanyName: string
  gCompanyContact: string
}

interface OpportunityStatusSelectProps {
  editable: boolean
  isPending: boolean
  statusState: OpportunityStatusState
  onStatusChange: (newStatusId: string) => void
  onCloseReasonChange: (newReasonId: string) => void
  onAdditionalCloseReasonChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onGCompanyNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onGCompanyContactChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  statuses: OpportunityStatus[]
  isSystemManaged: boolean
  groupedCloseReasons: Record<string, CloseReason[]>
  isClosed: boolean
  isGCompanyFlow: boolean
  isAdditionalCloseReasonRequired: boolean
  hintText: string
  errors: Record<string, string>
  isLoading: boolean
}

export function OpportunityStatusSelect({
  editable,
  isPending,
  statusState,
  onStatusChange,
  onCloseReasonChange,
  onAdditionalCloseReasonChange,
  onGCompanyNameChange,
  onGCompanyContactChange,
  statuses,
  isSystemManaged,
  groupedCloseReasons,
  isClosed,
  isGCompanyFlow,
  isAdditionalCloseReasonRequired,
  hintText,
  errors,
  isLoading,
}: OpportunityStatusSelectProps) {
  if (isLoading) {
    return <div className='text-sm text-muted-foreground'>読み込み中...</div>
  }

  return (
    <div className='space-y-4'>
      {/* ステータス選択 */}
      <div className='space-y-2'>
        <Label className='w-fit text-xs text-muted-foreground'>オポチュニティの状況</Label>
        <AppSelectPopover
          currentValue={String(statusState.statusId)}
          options={statuses
            .filter((s) => !s.is_system_managed || s.id === statusState.statusId)
            .map(
              (s): SelectOption => ({
                value: String(s.id),
                label: s.name,
                color: getColor(COLOR_CATEGORY.OPPORTUNITY_STATUS, s.id),
              }),
            )}
          onSelect={(value) => onStatusChange(value)}
          disabled={!editable || isPending || isSystemManaged}
          align='start'
        />
        {hintText && <p className='text-xs text-muted-foreground'>{hintText}</p>}
      </div>

      {/* クローズ理由（Closed状態または既にクローズ理由がある場合） */}
      {(isClosed || statusState.closeReasonId) && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <AppSelect
            id='closeReason'
            label='クローズ理由'
            required={isClosed}
            value={statusState.closeReasonId?.toString() || ''}
            onValueChange={onCloseReasonChange}
            disabled={!editable || !isClosed || isPending}
            placeholder='クローズ理由を選択'
            groups={Object.entries(groupedCloseReasons).map(
              ([category, reasons]): AppSelectGroup => ({
                label: category,
                options: reasons.map((reason) => ({
                  value: reason.id.toString(),
                  label: reason.closeReason,
                })),
              }),
            )}
            error={errors.closeReason}
          />
        </div>
      )}

      {/* 追加クローズ理由（その他理由選択時） */}
      {isAdditionalCloseReasonRequired && (
        <AppTextarea
          id='additionalCloseReason'
          label='クローズ理由（詳細）'
          required
          value={statusState.additionalCloseReason}
          onChange={onAdditionalCloseReasonChange}
          disabled={!editable || !isClosed || isPending}
          rows={3}
          maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION}
          placeholder='クローズ理由の詳細を入力してください'
          showCharCount
          error={errors.additionalCloseReason}
        />
      )}

      {/* G会社情報（G会社商流選択時） */}
      {isGCompanyFlow && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <AppInput
            id='gCompanyName'
            label='G会社名'
            required
            value={statusState.gCompanyName}
            onChange={onGCompanyNameChange}
            disabled={!editable || isPending}
            placeholder='G会社名を入力'
            error={errors.gCompanyName}
          />
          <AppInput
            id='gCompanyContact'
            label='G社担当者'
            required
            value={statusState.gCompanyContact}
            onChange={onGCompanyContactChange}
            disabled={!editable || isPending}
            placeholder='G社担当者名を入力'
            error={errors.gCompanyContact}
          />
        </div>
      )}
    </div>
  )
}
